// Marketing site content for ChooseRightCareer (features, testimonials, forms)
import { CDN, MARKETING } from './cdnImages';
import { SOCIAL_LINKS } from './socialLinks';

/** Square crop portraits for review cards (Unsplash — illustrative, not verified identities). */
const RP = (photoSlug, w = 280) =>
  `https://images.unsplash.com/${photoSlug}?ixlib=rb-4.0.3&auto=format&fit=crop&w=${w}&h=${w}&q=82`;

export const features = [
  {
    id: 1,
    title: "Built on the Career DNA framework",
    description:
      "Uses a structured seven trait model to understand how you think and decide, not only what you score on paper",
    icon: "Microscope"
  },
  {
    id: 2,
    title: "AI that listens to your patterns",
    description:
      "Surfaces habits in how you focus, bounce back, and choose so the read feels personal instead of generic",
    icon: "Brain"
  },
  {
    id: 3,
    title: "Quick, simple, and still deep",
    description: "Meaningful insights in about 8 to 10 minutes without a never ending form",
    icon: "Zap"
  },
  {
    id: 4,
    title: "Careers that actually fit you",
    description: "Explore options based on how you work and what energises you, not only the usual shortlist",
    icon: "Target"
  },
  {
    id: 5,
    title: "Reports your family can follow",
    description: "Clear PDF language so you can sit with parents or mentors and point to real examples",
    icon: "BookOpen"
  },
  {
    id: 6,
    title: "Humans when you want backup",
    description: "Optional counsellors help you turn the report into a plan that feels doable this week",
    icon: "Users"
  }
];

export const howItWorks = [
  {
    id: 1,
    step: "Step 1",
    title: "Tell us how you really work",
    description: "You move through a focused set of questions in about 8 to 10 minutes, with breaks if you need them",
    icon: "ClipboardList"
  },
  {
    id: 2,
    step: "Step 2",
    title: "AI plus expert shaped logic",
    description: "Your answers are mapped with structured rules and lived experience from counsellors and researchers",
    icon: "FileText"
  },
  {
    id: 3,
    step: "Step 3",
    title: "Your Career DNA report lands",
    description: "You receive a calm breakdown of what fits, what to watch, and what to explore next with confidence",
    icon: "TrendingUp"
  }
];

export const problemPoints = [
  {
    id: 1,
    title: "Start with you, not the crowd",
    description: "Honest questions about how you focus, recharge, and decide when things get messy.",
    icon: "User"
  },
  {
    id: 2,
    title: "See your Career DNA in plain words",
    description: "AI plus expert shaped logic turns your answers into patterns you can recognise in real life.",
    icon: "Brain"
  },
  {
    id: 3,
    title: "Open a wider map of careers",
    description: "Design, sport, stage, tech, business, public service, and more, filtered by what matches your style.",
    icon: "Compass"
  }
];

export const expertCounsellors = [
  {
    id: 1,
    slug: 'mitali-saha',
    name: "Mitali Saha",
    role: "There for marks stress and real family conversations",
    expertise: "Specialized in student behaviour psychology and career-related stress management",
    experience: "22+ years",
    image: MARKETING.counsellorMitaliSaha,
    story:
      "When marks week hits and everyone talks like your future is already signed, the stress is not drama, it is real. Mitali has sat in that seat with students for twenty-plus years, helping them turn worry into words they can actually use at home. No hero speech, no jargon, just clear language so the next family talk feels like you are in the room, not locked outside it.",
  },
  {
    id: 2,
    slug: 'shreya',
    name: 'Shreya',
    role: "Builds the plan you and your parents both trust",
    expertise: "Strengths mapped to a plan you and your parents can both trust.",
    experience: '10+ years',
    image: MARKETING.counsellorShreya,
    story:
      "You and your parents both want a plan, but sometimes you are not even arguing about the same fear: one side wants safety, the other wants air. Shreya starts from your Career DNA so the conversation stops being pure vibes, then builds steps and timelines you can all point at. Students say the relief is simple: nobody has to pretend they agree on a guess anymore.",
  },
  {
    id: 3,
    slug: 'rohit',
    name: 'Rohit',
    role: "Listens first, explains without the lecture",
    expertise: "How you think, worry, and choose — made concrete for you and your family.",
    experience: '10+ years',
    image: MARKETING.counsellorRohit,
    story:
      "I am not here to repeat the stream lecture you already heard in assembly. I want to know what pulls you when nobody is grading you, what drains you, and what you are scared to say out loud. We use your report like a map you and your family can read together, with examples instead of slogans, and we keep next steps small on purpose so they feel like yours, not a life copied from someone else’s reel.",
  },
  {
    id: 4,
    slug: 'riya',
    name: 'Dr. Riya Saha',
    role: "Straight talk on streams, expectations, and you",
    expertise: "Stream choice and family expectations — honest, medical-background lens.",
    experience: '7+ years',
    image: MARKETING.counsellorRiya,
    story:
      "If dinner-table talk is only doctor-engineer shorthand and your chest tightens because that is not your picture, you are not ungrateful. You are just crowded out. I help you name what you want in language that still respects your family, then shape a path you can own. The goal is not rebellion for show, it is a future you can live inside without pretending.",
  },
  {
    id: 5,
    slug: 'vikram-rao',
    name: "Vikram Rao",
    role: "Explores paths when one stream never felt like you",
    expertise: "Outside the usual three streams — hybrid and creative paths, no hype.",
    experience: "8+ years",
    image: CDN.counsellors[0],
    story:
      "You liked ideas that do not match the usual three-stream story, then felt guilty for being 'all over the place.' Vikram treats that mix as a clue, not a flaw: creative, hybrid, entrepreneurial routes, with straight talk on how to try things without betting your whole identity on one label. Students tell him it is the first time someone sounded curious about their weird combo instead of asking them to shrink into a box.",
  },
];

export const iitResearchers = [
  {
    id: 1,
    name: "Rahul Verma",
    institution: "IIT Bombay",
    role: "AI & Data Science Research"
  },
  
  {
    id: 2,
    name: "Priya Nair",
    institution: "IIT Delhi",
    role: "Psychology Framework Design"
  },
  
  {
    id: 3,
    name: "Ankit Sharma",
    institution: "IIT Madras",
    role: "Assessment Algorithm Development"
  }
];

/** Student quotes: composite-style stories, edited for length—illustrative, not individual verified reviews. */
export const testimonials = [
  {
    id: 1,
    name: 'Aman.',
    class: 'Class 12',
    location: 'Mumbai',
    text: 'I rolled my eyes at “another assessment” but the traits bit was weirdly accurate, like someone watched me cram vs when I actually focus. Still not 100 percent sure what I want, but I can explain my tradeoffs to my parents without crying now.',
    rating: 5,
    avatarUrl: MARKETING.testimonialAman,
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 2,
    name: 'Jyotika',
    class: 'Class 10',
    location: 'Gujrat',
    text: 'Before using Choose Right Career, I had no idea what I wanted to do in the future. The platform made things simple and easy to understand for me. It helped me discover my interests and gave me confidence to start planning early.',
    rating: 4,
    avatarUrl: MARKETING.testimonialJyotika,
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 3,
    name: 'Sneha',
    class: 'Class 12',
    location: 'Bengaluru',
    text: 'Mom needed something on paper. This was clear enough that she got why “everyone does coding” is not automatically for me. The counsellor session helped more than the report alone if I am honest.',
    rating: 5,
    avatarUrl: MARKETING.testimonialSneha ,
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 4,
    name: 'Dhruv',
    class: 'Class 12',
    location: 'Ranchi',
    text: 'Simple, clear, and genuinely helpful. Choose Right Career made it easy for me to understand what’s right for me without any confusion.',
    rating: 4,
    avatarUrl: MARKETING.testimonialDhruv,
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 5,
    name: 'Aashima',
    class: 'Class 9',
    location: 'Jamshedpur',
    text: 'I’m from ICSE, and choosing subjects in Class 9 felt really overwhelming. I wasn’t sure what I was good at or what would be right for me long-term. Choose Right Career made things simple and helped me understand my strengths clearly. Now I feel much more confident about the choices I’ve made.',
    rating: 5,
    avatarUrl: MARKETING.testimonialAashima,
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 6,
    name: 'Piyush',
    class: 'Class 11',
    location: 'Patna',
    text: "I got clarity and even discovered options I didn’t know existed and honestly I was so confused if I had chosen the right stream. Beech me itna advice mil raha tha ki aur zyada confusion ho gaya. After using ChooseRightCareer, . Ab lagta hai direction clear hai and I’m actually happy with my choice.",
    rating: 4,
    avatarUrl: MARKETING.testimonialPiyush,
    socialUrl: SOCIAL_LINKS.instagram,
  },
];

/** School side: role first line, org/region second. Illustrative composite feedback. */
export const schoolTestimonials = [
  {
    id: 's1',
    name: 'Career guidance lead',
    role: 'Senior secondary wing',
    location: 'Private school, South India',
    text: 'Before this, every teacher had a different “safe career” speech. Now we anchor conversations to the same PDF language. Parents still push, but at least it is the same data in PTA and in my office.',
    rating: 5,
    avatarUrl: RP('photo-1573496359142-b8d87734a5a2'),
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 's2',
    name: 'Principal’s office coordinator',
    role: 'Admin + student wellness',
    location: 'Metro school, West India',
    text: 'We ran a pilot class. Counsellors said prep time dropped because they were not inventing worksheets from scratch. Board asks for proof, we have cohort summaries that do not feel made up overnight.',
    rating: 5,
    avatarUrl: RP('photo-1560250097-0b93528c311a'),
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 's3',
    name: 'Parent representative',
    role: 'PTA core group',
    location: 'Tier 2 city',
    text: 'I am usually sceptical of paid programs. My kid’s report was not wow magical, but it was readable and it matched what I see at home about stress and focus. That made the fee conversation easier.',
    rating: 4,
    avatarUrl: RP('photo-1580489944761-15a19d654956'),
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 's4',
    name: 'Vice principal',
    role: 'Academics',
    location: 'CBSE school',
    text: 'We do not want another vendor who disappears. Rollout check in calls actually happened on the weeks they said. Still tweaking how we slot it before pre boards, but the partnership tone felt grounded.',
    rating: 5,
    avatarUrl: RP('photo-1472099645785-5658abf4ff4e'),
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 's5',
    name: 'School counsellor',
    role: 'Grades 9 to 12',
    location: 'North India',
    text: 'I was worried it would add more forms. Instead I got talking points I reuse in sessions. Not everything in the PDF fits every student, nothing can, but it is a better starting document than what we had.',
    rating: 4,
    avatarUrl: RP('photo-1594744803329-e58b31de8bf5'),
    socialUrl: SOCIAL_LINKS.instagram,
  },
  {
    id: 's6',
    name: 'STEM coordinator',
    role: 'Innovation lab lead',
    location: 'International curriculum school',
    text: 'Students compare streams like sports teams here. Having one neutral framework calmed some of the influencer level advice floating on Instagram. Wish analytics export was simpler, small gripe.',
    rating: 4,
    avatarUrl: RP('photo-1573497019940-1c28c88b4f3e'),
    socialUrl: SOCIAL_LINKS.instagram,
  },
];

// Lead form handler (Web3Forms when configured; otherwise local demo save)
export const submitLeadForm = (formData) => {
  if (process.env.NODE_ENV === 'development') console.log('Lead form submitted:', formData);
  
  // Simulate API call
  return new Promise((resolve) => {
    setTimeout(() => {
      // Store in localStorage for demo purposes
      const existingLeads = JSON.parse(localStorage.getItem('chooseRightCareerLeads') || '[]');
      const newLead = {
        ...formData,
        id: Date.now(),
        timestamp: new Date().toISOString()
      };
      existingLeads.push(newLead);
      localStorage.setItem('chooseRightCareerLeads', JSON.stringify(existingLeads));
      
      resolve({
        success: true,
        message: 'Thank you. We typically respond within within 24 hours on WhatsApp or email.',
      });
    }, 800);
  });
};

// School partnership form handler (Web3Forms when configured; otherwise local demo save)
export const submitSchoolForm = (formData) => {
  if (process.env.NODE_ENV === 'development') console.log('School partnership form submitted:', formData);
  
  return new Promise((resolve) => {
    setTimeout(() => {
      const existingRequests = JSON.parse(localStorage.getItem('schoolRequests') || '[]');
      const newRequest = {
        ...formData,
        id: Date.now(),
        timestamp: new Date().toISOString()
      };
      existingRequests.push(newRequest);
      localStorage.setItem('schoolRequests', JSON.stringify(existingRequests));
      
      resolve({ success: true, message: 'Thank you! Our team will reach out shortly.' });
    }, 800);
  });
};
